import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { APIProvider, Map, Marker, Circle } from '@vis.gl/react-google-maps'
import { MapPin } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export interface LatLng {
  lat: number
  lng: number
}

export interface CoverageMapProps {
  /** Map + circle center (technician base location). */
  center: LatLng
  /** Coverage radius in kilometres. */
  radiusKm: number
  /** Optional labels for named coverage zones, shown as badges below the map. */
  zoneLabels?: string[]
  /** Map height (any CSS length). Defaults to 320px. */
  height?: number | string
  className?: string
}

// Approximate metres-per-degree for a sensible initial zoom from the radius.
function zoomForRadiusKm(radiusKm: number): number {
  if (radiusKm <= 0) return 12
  // Each zoom level out roughly doubles the visible span. Anchor: ~5km -> z12.
  const z = 12 - Math.log2(Math.max(radiusKm, 1) / 5)
  return Math.min(15, Math.max(6, Math.round(z)))
}

// Renders a Google map with a circle for a technician's coverage area (#76).
// Falls back to a graceful placeholder when no API key is configured, so the
// surrounding form still renders in environments without VITE_GOOGLE_MAPS_API_KEY.
export function CoverageMap({
  center,
  radiusKm,
  zoneLabels,
  height = 320,
  className,
}: CoverageMapProps) {
  const { t } = useTranslation('shared')
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
  const style = useMemo(
    () => ({
      width: '100%',
      height: typeof height === 'number' ? `${height}px` : height,
    }),
    [height]
  )

  const zones = zoneLabels && zoneLabels.length > 0 && (
    <div className='space-y-1'>
      <p className='text-xs font-medium text-muted-foreground'>
        {t('coverageMap.zones')}
      </p>
      <div className='flex flex-wrap gap-1'>
        {zoneLabels.map(z => (
          <Badge key={z} variant='secondary'>
            {z}
          </Badge>
        ))}
      </div>
    </div>
  )

  if (!apiKey) {
    return (
      <div className={cn('space-y-2', className)}>
        <div
          style={style}
          className='flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground'
        >
          <MapPin className='h-6 w-6' />
          <p>{t('coverageMap.noApiKey')}</p>
          <p className='px-1 text-xs leading-4'>
            {t('coverageMap.radius', { radius: radiusKm })}
          </p>
        </div>
        {zones}
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div
        style={style}
        className='overflow-hidden rounded-md border border-border'
      >
        <APIProvider apiKey={apiKey}>
          <Map
            defaultCenter={center}
            defaultZoom={zoomForRadiusKm(radiusKm)}
            gestureHandling='cooperative'
            disableDefaultUI={false}
            style={{ width: '100%', height: '100%' }}
          >
            <Marker position={center} />
            <Circle
              center={center}
              radius={radiusKm * 1000}
              strokeColor='#2563eb'
              strokeOpacity={0.8}
              strokeWeight={2}
              fillColor='#2563eb'
              fillOpacity={0.12}
            />
          </Map>
        </APIProvider>
      </div>
      <p className='px-1 pt-1 text-xs leading-4 text-muted-foreground'>
        {t('coverageMap.radius', { radius: radiusKm })}
      </p>
      {zones}
    </div>
  )
}

export default CoverageMap
